import {
  READ_TIMEOUT_MESSAGE,
  WRITE_TIMEOUT_MESSAGE,
  translateServerMessage,
} from "./server-messages";

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "https://api-staging.aetherpush.com";

const AUTH_STORAGE_KEY = "aether_access_key";

export interface ApiFieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  status: number;
  error?: string;
  errors?: ApiFieldError[];
  code?: string;
  reasons?: string[];
  rawError?: string;

  constructor(
    status: number,
    body: { error?: string; errors?: ApiFieldError[]; code?: string; reasons?: string[] },
  ) {
    super(body.error ?? `API error ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.rawError = body.error;
    this.error = translateServerMessage(body.error);
    this.errors = body.errors?.map((fieldError) => ({
      ...fieldError,
      message: translateServerMessage(fieldError.message),
    }));
    this.code = body.code;
    this.reasons = body.reasons;
  }
}

export const DASHBOARD_SESSION_REQUIRED_CODE = "dashboard_session_required";
export const DEPLOYMENT_KEY_IN_USE_CODE = "deployment_key_in_use";
export const DEPLOYMENT_NAME_IN_USE_CODE = "deployment_name_in_use";
export const REQUEST_TIMEOUT_CODE = "request_timeout";

type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export class ApiTimeoutError extends ApiError {
  readonly retrySafe: boolean;

  constructor(retrySafe: boolean) {
    super(0, {
      error: retrySafe ? READ_TIMEOUT_MESSAGE : WRITE_TIMEOUT_MESSAGE,
      code: REQUEST_TIMEOUT_CODE,
    });
    this.name = "ApiTimeoutError";
    this.retrySafe = retrySafe;
  }
}

export function isRequestTimeout(error: unknown): boolean {
  return error instanceof ApiError && error.code === REQUEST_TIMEOUT_CODE;
}

export function isUncertainWriteTimeout(error: unknown): boolean {
  return error instanceof ApiTimeoutError && !error.retrySafe;
}

export function isUncertainWriteFailure(error: unknown): boolean {
  if (isUncertainWriteTimeout(error)) return true;
  if (error instanceof ApiError) return error.status >= 500;
  return error instanceof TypeError;
}

export class AccessKeyHandoffError extends Error {
  constructor() {
    super("The access key is changing in another tab.");
    this.name = "AccessKeyHandoffError";
  }
}

let accessKeyHandoff = false;
let accessKeyHandoffHeld = false;

export function beginAccessKeyHandoff(): void {
  accessKeyHandoff = true;
}

export function holdAccessKeyHandoff(): void {
  accessKeyHandoff = true;
  accessKeyHandoffHeld = true;
}

export function endAccessKeyHandoff(): void {
  if (accessKeyHandoffHeld) return;
  accessKeyHandoff = false;
}

export function resetAccessKeyHandoff(): void {
  accessKeyHandoff = false;
  accessKeyHandoffHeld = false;
}

export function isAccessKeyHandoff(): boolean {
  return accessKeyHandoff;
}

export function isAccessKeyHandoffHeld(): boolean {
  return accessKeyHandoffHeld;
}

export function isStaleSessionError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    error.code === DASHBOARD_SESSION_REQUIRED_CODE
  );
}

export function isSessionEndedError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401 && getAccessKey() === null;
}

export function getAccessKey(): string | null {
  try {
    return localStorage.getItem(AUTH_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setAccessKey(key: string): void {
  localStorage.setItem(AUTH_STORAGE_KEY, key);
}

export function clearAccessKey(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

export function onAccessKeyChange(handler: () => void): () => void {
  const listener = (event: StorageEvent) => {
    if (event.storageArea !== localStorage) return;
    if (event.key !== null && event.key !== AUTH_STORAGE_KEY) return;
    handler();
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
}

const REQUEST_TIMEOUT_MS = 30_000;

interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  anonymous?: boolean;
  keepalive?: boolean;
  timeoutMs?: number;
  retrySafe?: boolean;
}

async function request<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  if (accessKeyHandoff) {
    throw new AccessKeyHandoffError();
  }

  const {
    body,
    headers = {},
    anonymous = false,
    keepalive = false,
    timeoutMs,
    retrySafe = method === "GET",
  } = options;

  const accessKey = getAccessKey();
  if (accessKey && !anonymous) {
    headers["Authorization"] = `Bearer ${accessKey}`;
  }

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const url = `${API_BASE_URL}${path}`;

  const controller = keepalive ? null : new AbortController();
  let timedOut = false;
  const timer = controller
    ? setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs ?? REQUEST_TIMEOUT_MS)
    : null;

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      keepalive,
      signal: controller?.signal,
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const json = await response.json().catch((cause) => {
      if (timedOut) throw cause;
      return {};
    });

    if (!response.ok) {
      if (
        response.status === 401 &&
        !anonymous &&
        json?.code !== "step_up_required" &&
        getAccessKey() === accessKey
      ) {
        clearAccessKey();
      }

      throw new ApiError(response.status, json);
    }

    return json as T;
  } catch (error) {
    if (timedOut && !(error instanceof ApiError)) {
      throw new ApiTimeoutError(retrySafe);
    }
    throw error;
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>("GET", path, options),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, { ...options, body }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PATCH", path, { ...options, body }),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PUT", path, { ...options, body }),

  delete: <T>(path: string, options?: RequestOptions) => request<T>("DELETE", path, options),
} as const;
